// The theme finder (ENG-3225; plan decision 23): once a week, one AI call
// groups an org's escalations into a few themes, each with a rule worth
// codifying, for the Head of Operations. The model names and describes the
// themes and says which escalations belong to each; code counts them.
import { OrgBaseInterface } from "../Shared/OrgBase.Interace";
import { PulseChannel } from "./Pulse.Facts";

/** Below this many escalations in a week there's no AI call. */
export const PULSE_THEMES_MIN_ESCALATIONS = 5;
/** The model reads at most this many of a week's escalations, newest first. */
export const PULSE_THEMES_MAX_ESCALATIONS = 150;
/** At most this many themes a week. */
export const PULSE_THEMES_MAX_THEMES = 5;
/** A week is tried at most this many times. */
export const PULSE_THEMES_MAX_ATTEMPTS = 3;
/** The card shows themes whose window ended at most this many days ago. */
export const PULSE_THEMES_MAX_AGE_DAYS = 14;
/** An escalation's reason, ticket title and short summary are cut to this many characters before the model reads them. */
export const PULSE_THEMES_TEXT_MAX = 200;
/** Its full summary is cut to this many (Mason, 6 October). */
export const PULSE_THEMES_SUMMARY_MAX = 400;

/** Word limits on what the model writes for a theme. */
export const PULSE_THEME_TEXT_LIMITS = { label: 5, description: 25, rule: 25 } as const;

/** Where one org's week stands. */
export enum PulseThemeStatus {
  /** A copy of the bot claimed it and is working on it. */
  RUNNING = "running",
  /** Themes written. */
  DONE = "done",
  /** Too few escalations for an AI call. */
  EMPTY = "empty",
  /** The call or its answer failed; tried again an hour later, up to PULSE_THEMES_MAX_ATTEMPTS. */
  FAILED = "failed"
}

/** One theme as saved: the model's words, and what code counted. */
export interface PulseThemeItem {
  /** A few words, no numbers. */
  label: string;
  /** What these customers needed. */
  description: string;
  /** One rule that would let the AI handle these itself. */
  rule: string;
  /** The escalations in it, counted by code from the model's grouping. Each escalation counts in one theme at most. */
  count: number;
  /** Up to 3 of its conversations, newest first. */
  examples: number[];
}

// LINK libs/entities/src/Pulse/PulseTheme.Entity.ts
/** One org's week of themes: the pulse_theme row. Never deleted. */
export interface PulseThemeInterface extends OrgBaseInterface {
  /** The UTC Monday of the week it was claimed in, yyyy-MM-dd. */
  weekStart: string;
  status: PulseThemeStatus;
  /** Claims so far, the first included. */
  attempts: number;
  /** The escalations read: from inclusive, to exclusive. */
  windowFrom: Date;
  windowTo: Date;
  /** Escalations in the window; null until counted. */
  escalations: number | null;
  /** Largest first; null until written. */
  themes: PulseThemeItem[] | null;
  /** Why the last attempt failed, display-safe. */
  error: string | null;
}

/** One escalated conversation as the theme finder reads it. Ticket descriptions never come, and free text is cut short. */
export interface PulseThemeEscalation {
  /** The conversation's id. */
  id: number;
  channel: PulseChannel;
  /** The location's name, when it has one. */
  location: string | null;
  /** The routing topic it was escalated under. */
  topic: string | null;
  /** Why the AI escalated, as it wrote it. The fixed reasons takeovers and managers write are left out. */
  reason: string | null;
  ticketTitle: string | null;
  shortSummary: string | null;
  /** The full summary, cut to PULSE_THEMES_SUMMARY_MAX characters. */
  summary: string | null;
}

/** A window's escalations for the theme finder (server to bot). */
export interface PulseThemeEscalations {
  /** Every escalation in the window, by the handoffs card's rule. */
  total: number;
  /** The newest, up to PULSE_THEMES_MAX_ESCALATIONS. */
  rows: PulseThemeEscalation[];
  /** The org's agents' names: themes naming one are dropped. */
  names: string[];
}

/** The org's latest themes, for the Head of Operations' card. */
export interface PulseEscalationThemes {
  weekStart: string;
  windowFrom: string;
  windowTo: string;
  /** Escalations in the window. */
  escalations: number;
  /** How many of them the model read: the newest, up to PULSE_THEMES_MAX_ESCALATIONS. */
  read: number;
  themes: PulseThemeItem[];
}
